import { useEffect, useState } from "react";
import { inServerText } from "@/components/network/StatusPill";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, ChevronRight, Cpu, Plus } from "lucide-react";
import { ONU_STATUS, SELECT_CLS, apiError } from "@/lib/network";

function OnuDialog({ target, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (target) setF({ serial_number: "", vendor: "", model: "", status: "TERPASANG", catatan: "", ...(target.onu || {}) }); }, [target]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const body = { customer_id: target.customer_id, serial_number: f.serial_number, vendor: f.vendor || "", model: f.model || "", status: f.status, catatan: f.catatan || "" };
    try {
      await (target.onu ? api.put(`/network/onu/${target.onu.id}`, body) : api.post("/network/onu", body));
      toast.success("Data ONU tersimpan");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="onu-dialog">
        <DialogHeader><DialogTitle>ONU — {target?.nama_customer || target?.customer_id}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-[#6B7280]">Inventaris manual. Tidak ada konfigurasi atau registrasi ke perangkat OLT.</p>
          <div><Label>Serial Number</Label><Input value={f.serial_number || ""} onChange={set("serial_number")} required data-testid="onu-serial" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Merek</Label><Input value={f.vendor || ""} onChange={set("vendor")} data-testid="onu-vendor" /></div>
            <div><Label>Model</Label><Input value={f.model || ""} onChange={set("model")} data-testid="onu-model" /></div>
          </div>
          <div><Label>Status</Label><select className={`${SELECT_CLS} w-full`} value={f.status || "TERPASANG"} onChange={set("status")} data-testid="onu-status">
            {ONU_STATUS.map((s) => <option key={s}>{s}</option>)}</select></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Batal</Button><Button type="submit" data-testid="onu-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Count = ({ n, label }) => (n ? <Missing>{n} {label}</Missing>
  : <span className="inline-flex items-center rounded bg-[#F0FDF4] px-1.5 text-[11px] text-[#15803D]">0 {label}</span>);

const Missing = ({ children, testid }) => (
  <span className="inline-flex items-center gap-1 rounded bg-[#FFFBEB] px-1.5 text-[11px] text-[#B45309]" data-testid={testid}>
    <AlertTriangle className="h-3 w-3" />{children}</span>
);

function Customer({ c, canManage, onOnu }) {
  return (
    <li className="flex flex-wrap items-center gap-2 py-0.5" data-testid={`topo-cust-${c.customer_id}`}>
      <span className="font-mono-tab text-[11px] text-[#6B7280]">port {c.port}</span>
      <span className="font-medium">{c.nama_customer || c.customer_id}</span>
      <span className="text-[11px] text-[#6B7280]">{c.customer_id}</span>
      {c.onu ? <span className="inline-flex items-center gap-1 rounded bg-[#EFF6FF] px-1.5 text-[11px] text-[#1D4ED8]" data-testid={`topo-onu-${c.customer_id}`}>
        <Cpu className="h-3 w-3" />{c.onu.serial_number}</span> : <Missing testid={`topo-noonu-${c.customer_id}`}>ONU belum didata</Missing>}
      {canManage && <button className="text-[11px] text-[#0055FF] hover:underline" onClick={() => onOnu(c)} data-testid={`topo-onu-edit-${c.customer_id}`}>
        {c.onu ? "Ubah ONU" : <span className="inline-flex items-center"><Plus className="h-3 w-3" />Isi ONU</span>}</button>}
    </li>
  );
}

const Ref = ({ n }) => <span className="text-[11px] text-[#6B7280]" data-testid={`topo-ref-${n.kode}`}>· sudah ditampilkan di jalur lain</span>;
const NoXY = ({ n }) => (n.in_server ? <span className="text-[11px] text-[#334155]" data-testid={`topo-inserver-${n.kode}`}> · {inServerText(n.in_server)}</span>
  : n.has_coords === false ? <span className="text-[11px] text-[#9CA3AF]"> · tanpa koordinat</span> : null);

// Registry agar komponen rekursif tidak saling-referensi langsung (menghindari overflow saat build).
const NODES = {};

function FedSplitters({ list, ...p }) {
  if (!list?.length) return null;
  return (
    <ul className="ml-4 border-l border-dashed pl-3">
      {list.map((sp) => <li key={sp.id}><span className="text-[11px] text-[#6B7280]">menyuplai input </span><NODES.Splitter sp={sp} {...p} /></li>)}
    </ul>
  );
}

function OdpNode({ o, canManage, onOnu }) {
  return (
    <div data-testid={`topo-odp-${o.kode}`}>
      <div><span className="font-mono-tab font-semibold">ODP {o.kode || "(terhapus)"}</span>{" "}
        {o.ref ? <Ref n={o} /> : <span className="text-xs text-[#6B7280]">{o.nama} · {o.pelanggan.length}/{o.kapasitas_port} port<NoXY n={o} /></span>}</div>
      {!o.ref && <>
        <ul className="ml-4 border-l pl-3">
          {o.tanpa_odc && <li><Missing testid={`topo-odp-no-odc-${o.kode}`}>ODP tanpa ODC di jalur upstream</Missing></li>}
          {!o.pelanggan.length && <li><Missing>Belum ada pelanggan</Missing></li>}
          {o.pelanggan.map((c) => <Customer key={c.customer_id} c={c} canManage={canManage} onOnu={onOnu} />)}
        </ul>
        <NODES.Fed list={o.splitters} canManage={canManage} onOnu={onOnu} />
      </>}
    </div>
  );
}

function OdcBranch({ odc, canManage, onOnu }) {
  if (odc.ref) return null;
  return (
    <ul className="ml-5 mt-1 space-y-1 border-l pl-3">
      {odc.tanpa_odp && <li><Missing testid={`topo-odc-no-odp-${odc.kode}`}>ODC belum memiliki ODP</Missing></li>}
      {odc.odp.map((o) => <li key={o.id}><NODES.Odp o={o} canManage={canManage} onOnu={onOnu} /></li>)}
      {odc.splitters?.length > 0 && <li><NODES.Fed list={odc.splitters} canManage={canManage} onOnu={onOnu} /></li>}
    </ul>
  );
}

function SplitterBranch({ sp, canManage, onOnu }) {
  return (
    <div className="inline-block align-top" data-testid={`topo-splitter-node-${sp.kode}`}>
      <span className="font-semibold">Splitter {sp.kode || "(terhapus)"}</span>{" "}
      {sp.ref ? <Ref n={sp} /> : <span className="text-[11px] text-[#6B7280]">{sp.rasio} · {sp.outputs.length}/{sp.jumlah_output} output<NoXY n={sp} /></span>}
      {!sp.ref && (
        <ul className="ml-5 mt-1 space-y-1 border-l pl-3">
          {!sp.outputs.length && <li><Missing>Splitter belum memiliki output</Missing></li>}
          {sp.outputs.map((o) => (
            <li key={o.output_id} data-testid={`topo-split-out-${sp.kode}-${o.output_port}`}>
              <span className="font-semibold">Output {o.output_port} <ChevronRight className="inline h-3.5 w-3.5 text-[#9CA3AF]" /></span>{" "}
              {o.target_kind === "splitter" ? <NODES.Splitter sp={o.target} canManage={canManage} onOnu={onOnu} />
                : o.target_kind === "odp" ? <div className="ml-1 inline-block align-top"><NODES.Odp o={o.target} canManage={canManage} onOnu={onOnu} /></div>
                : <><span className="font-semibold">ODC {o.odc.kode || "(terhapus)"}</span>{" "}
                    {o.odc.ref ? <Ref n={o.odc} /> : <span className="text-[#6B7280]">{o.odc.nama}<NoXY n={o.odc} /></span>}
                    <NODES.Odc odc={o.odc} canManage={canManage} onOnu={onOnu} /></>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

Object.assign(NODES, { Splitter: SplitterBranch, Odp: OdpNode, Odc: OdcBranch, Fed: FedSplitters });

export function OltTopology({ oltId, canManage, reloadKey }) {
  const [t, setT] = useState(null);
  const [onu, setOnu] = useState(null);
  const [k, setK] = useState(0);
  useEffect(() => { api.get(`/network/olt/${oltId}/topology`).then((r) => setT(r.data)).catch(() => toast.error("Gagal memuat topologi")); }, [oltId, reloadKey, k]);
  if (!t) return <p className="p-3 text-xs">Memuat topologi…</p>;
  const w = t.incomplete;
  return (
    <div className="space-y-2 text-sm" data-testid="olt-topology">
      {t.olt.in_server && <div className="text-xs text-[#334155]" data-testid="topo-olt-inserver">Lokasi OLT: {inServerText(t.olt.in_server)}</div>}
      {t.olt.status === "PERBAIKAN" && (
        <div className="rounded-md border border-[#FCD34D] bg-[#FEF3C7] px-3 py-2 text-xs font-semibold text-[#92400E]" role="status" data-testid="topo-olt-repair-banner">
          OLT dalam PERBAIKAN — relasi fisik tetap tercatat, layanan mungkin terganggu.</div>
      )}
      <div className="flex flex-wrap gap-2 text-[11px]" data-testid="topo-summary">
        <Count n={w.port_tanpa_odc} label="port tanpa ODC" /><Count n={w.odc_tanpa_odp} label="ODC tanpa ODP" /><Count n={w.odp_tanpa_odc || 0} label="ODP tanpa ODC" />
        <Count n={w.odp_tanpa_pelanggan} label="ODP tanpa pelanggan" /><Count n={w.pelanggan_tanpa_onu} label="pelanggan tanpa ONU" />
        <Count n={w.splitter_tanpa_odc || 0} label="splitter tanpa output" />
      </div>
      {!t.ports.length && <p className="text-xs text-[#6B7280]">OLT ini belum memiliki port PON.</p>}
      <ul className="space-y-1">
        {t.ports.map((p) => (
          <li key={p.id} className="rounded border border-[#E5E7EB] p-2" data-testid={`topo-port-${p.nama_port}`}>
            <div className="flex flex-wrap items-start gap-2 font-semibold"><span className="font-mono-tab">PON {p.nama_port}</span>
              <ChevronRight className="mt-0.5 h-3.5 w-3.5 text-[#9CA3AF]" />
              {p.odc ? <span data-testid={`topo-odc-${p.nama_port}`}>ODC {p.odc.kode} <span className="font-normal text-[#6B7280]">{p.odc.nama}<NoXY n={p.odc} /></span></span>
                : p.splitter ? <div className="font-normal" data-testid={`topo-splitter-${p.nama_port}`}><SplitterBranch sp={p.splitter} canManage={canManage} onOnu={setOnu} /></div>
                : <Missing testid={`topo-noodc-${p.nama_port}`}>Belum terhubung ke ODC/splitter</Missing>}</div>
            {p.odc && <OdcBranch odc={p.odc} canManage={canManage} onOnu={setOnu} />}
          </li>
        ))}
      </ul>
      <OnuDialog target={onu} onClose={() => setOnu(null)} onSaved={() => { setOnu(null); setK((x) => x + 1); }} />
    </div>
  );
}
